/**
 * Public-website content API client (task 5.1, public-website spec).
 *
 * Wraps `ApprovedContentApi.GetApprovedContentV1` (the **live**
 * public REST operation per `services/src/api-layer/handlers/get-
 * content-handler.ts` / `infra/src/stacks/api-layer-stack.ts`) into
 * the two SPA-side function shapes the slices and pages already
 * consume:
 *
 *   - `getContent({ date })`     → returns a `GetContentV1Output`
 *   - `listDaysWithSummaries({ page, pageSize })` → returns a
 *     `ListDaysWithSummariesOutput`
 *
 * Both shapes mirror the design §3.3 Smithy structures verbatim. The
 * **transport** is composed client-side: `getContent` issues three
 * parallel `POST /GetApprovedContentV1` calls (one each for `type:
 * "fact" | "news" | "release"` against the same `from === to ===
 * date` window) and assembles the result; `listDaysWithSummaries`
 * pulls the most-recent N days of releases / news / facts and
 * buckets them per date into the `DaySummary` rows the SPA renders.
 *
 * Why client-side composition: the Smithy IDL design § 3.3 specifies
 * `GetContentV1` and `ListDaysWithSummariesV1` as the canonical
 * server-side aggregations, but `smithy-api-layer` follow-up A —
 * which adds those two operations to the live REST gateway — has
 * not yet landed. The DynamoDB tables already carry the records,
 * and `GetApprovedContentV1` already serves them filtered by the
 * consumer-visible status set (Property 6). Composing client-side
 * keeps the SPA unblocked without touching the api-layer / models
 * trees. When follow-up A lands, this file shrinks back to two
 * direct `swrFetch` calls — the function signatures, exported
 * names, and every consumer callsite stay identical.
 *
 * Per the tasks.md "Implementation gating" preamble, the
 * `smithy-api-layer` follow-up A is the canonical long-term path;
 * this client-side composition is the short-circuit so the SPA
 * renders content in the meantime.
 *
 * The SPA never constructs the API origin directly: every request
 * goes through the CloudFront `/api/*` cache behavior (task 3.4) so
 * the 15-minute api-passthrough cache and the per-`/Profile*V1`
 * bypass behaviors apply uniformly. Callers pass `baseUrl: "/api"`
 * (production) or the dev-server proxy origin (local).
 *
 * Function-length budget: every public export below stays under 20
 * lines per Requirement 3.7.
 */

import type { ApprovedContentItem } from '@dvgup/contracts/generated/approved-content-api';
import { formatDisplayDateSmartYear } from '../format/format-display-date';
import { swrFetch, type SwrSubscription } from './swr-cache';

/** Re-export so consumers (slices, components) can `import` from one place. */
export type { ApprovedContentItem };

/** Page size the live `GetApprovedContentV1` pins per parent §4.1 / R8.3. */
const APPROVED_CONTENT_PAGE_SIZE = 20;

/**
 * Default look-back window for `listDaysWithSummaries`. Each requested
 * page covers `LIST_DAYS_WINDOW_DAYS` calendar days back from today;
 * `page === 1` is the most-recent window, `page === 2` is the window
 * before that, etc. 90 days × 1 page = a quarter of content per
 * paginator click, which keeps the per-call latency bounded while the
 * server-side aggregation is still owed by `smithy-api-layer`
 * follow-up A.
 */
const LIST_DAYS_WINDOW_DAYS = 90;

/**
 * Live RPC response shape for `POST /GetApprovedContentV1` per
 * `services/src/api-layer/managers/get-content-manager.ts` —
 * `{ items, page, pageSize, total, hasMore }`. Re-declared here as
 * an internal transport type; not exported because the SPA-facing
 * surface is `GetContentV1Output` + `ListDaysWithSummariesOutput`
 * below.
 */
interface ApprovedContentResponse {
  readonly items: ReadonlyArray<ApprovedContentItem>;
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly hasMore: boolean;
}

/**
 * Per-day summary card shown on `/days` and on the Daily-page
 * fallback (Requirement 1.6). Mirrors the design §3.3 Smithy
 * `DaySummary` structure with an additional `images` array so the
 * Days page can slideshow through the day's full set of artwork on
 * hover. The Smithy contract evolution to carry a full image set
 * is owed by `smithy-api-layer` follow-up A; until then the SPA
 * composes `images` client-side from the release / news / fact
 * encoded-body envelopes.
 */
export interface DaySummary {
  /** YYYY-MM-DD per design §3.3. */
  readonly date: string;
  readonly title: string;
  /** Absolute URL to the primary image (the slideshow's first frame). */
  readonly primaryImage: string;
  /**
   * Every image URL associated with the day (release covers + news
   * + fact). Includes `primaryImage` as the first entry. Empty when
   * no images are available.
   */
  readonly images: ReadonlyArray<string>;
  readonly hookPhrase: string;
}

/**
 * Input shape for `ListDaysWithSummariesV1` per design §3.3. Both
 * fields are optional per the IDL — `page` defaults to 1 server-side
 * and `pageSize` is server-pinned to 20 (Property 17).
 */
export interface ListDaysWithSummariesInput {
  /** 1-indexed page; server defaults to 1 when omitted. */
  readonly page?: number;
  /** Server-pinned to 20 per Property 17; sent only for explicitness. */
  readonly pageSize?: number;
}

/** Output shape for `ListDaysWithSummariesV1` per design §3.3. */
export interface ListDaysWithSummariesOutput {
  readonly items: ReadonlyArray<DaySummary>;
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly hasMore: boolean;
}

/** Input shape for `GetContentV1` per design §3.3. */
export interface GetContentV1Input {
  /** YYYY-MM-DD per design §3.3 (Smithy `@pattern("^\\d{4}-\\d{2}-\\d{2}$")`). */
  readonly date: string;
}

/** Output shape for `GetContentV1` per design §3.3 (the Daily-page payload). */
export interface GetContentV1Output {
  readonly date: string;
  readonly title: string;
  readonly primaryImage: string;
  readonly hookPhrase: string;
  readonly releases: ReadonlyArray<ApprovedContentItem>;
  readonly news: ReadonlyArray<ApprovedContentItem>;
  readonly factOfTheDay: ApprovedContentItem;
}

/**
 * Per-call options for the content API client. The caller threads the
 * CloudFront-fronted base URL and the public REST API key through
 * (read from `/config.json` at SPA bootstrap per design §3.1).
 */
export interface ContentApiClientOptions {
  /** API origin — `"/api"` in production, dev-server proxy origin locally. */
  readonly baseUrl: string;
  /** Public REST API key from `/config.json`; injected as `x-api-key`. */
  readonly apiKey?: string;
}

/**
 * Issue one `POST /GetApprovedContentV1` request and return the
 * parsed `ApprovedContentResponse` page.
 */
function fetchApprovedContent(
  type: 'fact' | 'news' | 'release',
  from: string,
  to: string,
  page: number,
  options: ContentApiClientOptions,
): Promise<ApprovedContentResponse> {
  return swrFetch<ApprovedContentResponse>(
    `${options.baseUrl}/GetApprovedContentV1`,
    {
      apiKey: options.apiKey,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, from, to, page }),
    },
  );
}

/**
 * Subtract `days` calendar days from an `YYYY-MM-DD` UTC date string.
 * Pure helper; does no I/O and tolerates malformed input by returning
 * the same string (the caller's `from` then equals `to` and the live
 * gateway treats that as a single-day query).
 */
function subtractDaysUtc(yyyymmdd: string, days: number): string {
  const parsed = new Date(`${yyyymmdd}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return yyyymmdd;
  parsed.setUTCDate(parsed.getUTCDate() - days);
  return parsed.toISOString().slice(0, 10);
}

/** First non-empty string from a list of candidates, or the fallback. */
function firstNonEmpty(candidates: ReadonlyArray<unknown>, fallback: string): string {
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return fallback;
}

/**
 * Round-21 envelope decomposition (smithy-api-layer Follow-up A
 * item 1). Pre-round-21 the api-layer forwarded the record's raw
 * DynamoDB `body` attribute unchanged, and that attribute was a
 * JSON-encoded envelope containing `title` / `image` / `gallery` /
 * `platforms` / `purchaseUrl` / `source` / etc. A client-side
 * `decodeBody` helper here parsed the envelope and augmented each
 * item with those fields.
 *
 * Round 21 moves the decoding to the server. `projectRecord` in
 * `services/src/api-layer/handlers/_entries/_adapters/
 * _adapter-helpers.ts` now parses the envelope and forwards the
 * structured fields as first-class `ApprovedContentItem`
 * properties per the updated Smithy IDL. This client no longer
 * needs to parse.
 *
 * `hydrateItem` remains as a thin compat shim that projects the
 * new `purchaseLinks` wire field onto the pre-round-21
 * `affiliateLinks` list shape read by `<ReleaseCard>`,
 * `<GamesPage>`, and `<DailyPage>`. Every other new field is
 * passed through unchanged. Once those consumers are migrated to
 * read `purchaseLinks` directly, `hydrateItem` collapses to
 * `identity`.
 */
function hydrateItem(item: ApprovedContentItem): ApprovedContentItem {
  const purchaseLinks = (item as {
    readonly purchaseLinks?: ReadonlyArray<{ readonly vendor: string; readonly url: string }>;
  }).purchaseLinks;
  if (!purchaseLinks || purchaseLinks.length === 0) return item;
  const existingLinks = Array.isArray(item.affiliateLinks) ? item.affiliateLinks : [];
  const augmentedLinks = [
    ...existingLinks,
    ...purchaseLinks.map((link) => ({
      asin: link.url,
      url: link.url,
      title: link.vendor,
      image: '',
    })),
  ];
  return { ...item, affiliateLinks: augmentedLinks } as unknown as ApprovedContentItem;
}

function hydrateItems(
  items: ReadonlyArray<ApprovedContentItem>,
): ReadonlyArray<ApprovedContentItem> {
  return items.map(hydrateItem);
}

/**
 * Bucket the items returned by the three parallel `GetApprovedContentV1`
 * calls into the `GetContentV1Output` shape `<DailyPage>` consumes.
 *
 * The Daily-page fallback path in `<DailyPage>` (Requirement 1.6)
 * relies on `isContentEmpty` returning true when ALL three buckets
 * are empty AND the fact-of-the-day slot is missing — so the
 * synthesized `factOfTheDay` is set to a sentinel empty fact when no
 * real fact exists for the date, and the empty-result branch in
 * `<DailyPage>` will trigger the prior-day pull.
 */
function composeGetContentOutput(
  date: string,
  facts: ReadonlyArray<ApprovedContentItem>,
  news: ReadonlyArray<ApprovedContentItem>,
  releases: ReadonlyArray<ApprovedContentItem>,
): GetContentV1Output {
  const fact = facts[0];
  const headline = firstNonEmpty(
    [(fact as { headline?: unknown } | undefined)?.headline, fact?.body, news[0]?.body],
    '',
  );
  const primaryImage = firstNonEmpty(
    [(releases[0] as { image?: unknown } | undefined)?.image],
    '',
  );
  return {
    date,
    title: headline.length > 0 ? `Daily for ${date}` : `Daily for ${date}`,
    primaryImage,
    hookPhrase: typeof fact?.body === 'string' ? fact.body : '',
    releases,
    news,
    // `factOfTheDay` is required by the Smithy contract; when no fact
    // has landed for the date the SPA's `isContentEmpty` helper
    // detects the empty body and triggers the Requirement 1.6
    // fallback. Synthesize an empty sentinel item so the type stays
    // satisfied without leaking the ApprovedContentItem | undefined
    // distinction into every component.
    factOfTheDay: fact ?? buildEmptyFact(date),
  };
}

/** Build an empty `ApprovedContentItem` fact sentinel for the given date. */function buildEmptyFact(date: string): ApprovedContentItem {
  return {
    id: undefined,
    type: 'fact',
    date,
    body: '',
    citations: [],
    affiliateLinks: [],
    metadata: undefined,
  } as unknown as ApprovedContentItem;
}

/**
 * Fetch a single day's content payload per design §3.3 / Requirement
 * 1.5. Used by `<DailyPage>` (today's date) and `<DayDetailPage>`
 * (`/daily/:date`).
 *
 * Composes three `GetApprovedContentV1` requests (`fact` / `news` /
 * `release`) for the same date and shapes the result into the
 * `GetContentV1Output` envelope the SPA's slices already consume.
 *
 * The optional `subscription` from the original `GetContentV1` shape
 * is intentionally dropped while the client-side composition is in
 * place — SWR revalidation already keeps each of the three underlying
 * `GetApprovedContentV1` cache entries fresh per request, and the
 * three responses don't share a single cache key. When
 * `smithy-api-layer` follow-up A lands the server-side aggregation,
 * the subscription parameter is reinstated on the single underlying
 * `GetContentV1` call.
 *
 * @param input The date to fetch (YYYY-MM-DD).
 * @param options Base URL + API key sourced from `/config.json`.
 * @returns The full Daily-page payload for the requested date.
 */
export async function getContent(
  input: GetContentV1Input,
  options: ContentApiClientOptions,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _subscription?: SwrSubscription<GetContentV1Output>,
): Promise<GetContentV1Output> {
  const { date } = input;
  const [factsRes, newsRes, releasesRes] = await Promise.all([
    fetchApprovedContent('fact', date, date, 1, options),
    fetchApprovedContent('news', date, date, 1, options),
    fetchApprovedContent('release', date, date, 1, options),
  ]);
  return composeGetContentOutput(
    date,
    hydrateItems(factsRes.items),
    hydrateItems(newsRes.items),
    hydrateItems(releasesRes.items),
  );
}

/**
 * Render the date into the title prefix shown on the day cards
 * (`"Daily for {label}"`). Uses the smart-year formatter so the
 * year is omitted for the current year.
 */
function formatBucketDateLabel(date: string): string {
  return formatDisplayDateSmartYear(date);
}

/**
 * Bucket the items returned by `GetApprovedContentV1` into per-date
 * `DaySummary` rows, ordered most-recent-first. Dates strictly
 * after `today` are filtered out so the listing never shows future
 * entries. The per-day `images` list aggregates every image URL
 * surfaced by the day's release / news / fact records (a release
 * day with two games yields two cover images plus a news image
 * plus a fact image, all suitable for a slideshow).
 */
function bucketDaysFromItems(
  facts: ReadonlyArray<ApprovedContentItem>,
  news: ReadonlyArray<ApprovedContentItem>,
  releases: ReadonlyArray<ApprovedContentItem>,
  today: string,
): ReadonlyArray<DaySummary> {
  type Slot = {
    fact?: ApprovedContentItem;
    news?: ApprovedContentItem;
    releases: ApprovedContentItem[];
  };
  const byDate = new Map<string, Slot>();
  const ensureSlot = (date: string): Slot => {
    const existing = byDate.get(date);
    if (existing) return existing;
    const created: Slot = { releases: [] };
    byDate.set(date, created);
    return created;
  };
  for (const f of facts) {
    if (typeof f.date === 'string' && f.date <= today) ensureSlot(f.date).fact = f;
  }
  for (const n of news) {
    if (typeof n.date === 'string' && n.date <= today) ensureSlot(n.date).news = n;
  }
  for (const r of releases) {
    if (typeof r.date === 'string' && r.date <= today) ensureSlot(r.date).releases.push(r);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([date, slot]) => buildDaySummary(date, slot));
}

/** Pluck `image` off a hydrated content item, or `undefined`. */
function pickImage(item: ApprovedContentItem | undefined): string | undefined {
  const candidate = (item as { image?: unknown } | undefined)?.image;
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : undefined;
}

/**
 * Build one `DaySummary` from a bucket slot — concatenates the
 * release covers (in input order) followed by the news image and
 * the fact image so the slideshow shows games first, then
 * supporting media.
 */
function buildDaySummary(
  date: string,
  slot: { fact?: ApprovedContentItem; news?: ApprovedContentItem; releases: ApprovedContentItem[] },
): DaySummary {
  const releaseImages = slot.releases
    .map(pickImage)
    .filter((url): url is string => typeof url === 'string');
  const newsImage = pickImage(slot.news);
  const factImage = pickImage(slot.fact);
  const images: string[] = [];
  for (const url of releaseImages) if (!images.includes(url)) images.push(url);
  if (newsImage && !images.includes(newsImage)) images.push(newsImage);
  if (factImage && !images.includes(factImage)) images.push(factImage);
  return {
    date,
    title: formatBucketDateLabel(date),
    primaryImage: images[0] ?? '',
    images,
    hookPhrase: typeof slot.fact?.body === 'string' ? slot.fact.body : '',
  };
}

/**
 * Fetch the day-summaries page per design §3.3 / Requirement 1.6.
 *
 * Composes three `GetApprovedContentV1` requests (`fact` / `news` /
 * `release`) over a 90-day rolling window per page (page 1 covers
 * the last 90 days, page 2 covers the 90 days before that, etc.)
 * and buckets the results into one `DaySummary` per date that has
 * any content. The buckets are pre-deduplicated so each date appears
 * exactly once.
 *
 * @param input The pagination input (`page` / `pageSize`, both optional).
 * @param options Base URL + API key sourced from `/config.json`.
 * @returns The day-summaries page (cached or freshly fetched).
 */
export async function listDaysWithSummaries(
  input: ListDaysWithSummariesInput,
  options: ContentApiClientOptions,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _subscription?: SwrSubscription<ListDaysWithSummariesOutput>,
): Promise<ListDaysWithSummariesOutput> {
  const page = input.page ?? 1;
  const today = new Date().toISOString().slice(0, 10);
  const to = subtractDaysUtc(today, (page - 1) * LIST_DAYS_WINDOW_DAYS);
  const from = subtractDaysUtc(today, page * LIST_DAYS_WINDOW_DAYS - 1);
  const [factsRes, newsRes, releasesRes] = await Promise.all([
    fetchApprovedContent('fact', from, to, 1, options),
    fetchApprovedContent('news', from, to, 1, options),
    fetchApprovedContent('release', from, to, 1, options),
  ]);
  const items = bucketDaysFromItems(
    hydrateItems(factsRes.items),
    hydrateItems(newsRes.items),
    hydrateItems(releasesRes.items),
    today,
  );
  return {
    items,
    page,
    pageSize: APPROVED_CONTENT_PAGE_SIZE,
    total: items.length,
    hasMore: factsRes.hasMore || newsRes.hasMore || releasesRes.hasMore,
  };
}

/**
 * Fetch a page of release records ending at `today` and going back
 * over a 90-day rolling window per page. Used by `<GamesPage>` to
 * power the reverse-chronological release listing — when the
 * `smithy-api-layer` follow-up A wires a real `SearchGamesV1` that
 * returns release records, this function shrinks back to a single
 * `swrFetch` call. Until then, we reuse the same
 * `GetApprovedContentV1 {type:"release"}` transport the Daily and
 * Days pages already exercise.
 *
 * Future-date guard: every returned item has `date <= today` so
 * future releases never leak into the listing per the operator's
 * "no future dates" requirement.
 *
 * Optional `q` filter is applied client-side against the encoded-
 * body title + body text — fine for the prototyping phase since the
 * current beta dataset only carries two distinct titles.
 *
 * @param input page index (1-indexed) + optional q.
 * @param options Base URL + API key.
 * @returns Hydrated release items + pagination metadata.
 */
export async function searchReleases(
  input: { readonly page: number; readonly q?: string },
  options: ContentApiClientOptions,
): Promise<{
  readonly items: ReadonlyArray<ApprovedContentItem>;
  readonly page: number;
  readonly hasMore: boolean;
}> {
  const today = new Date().toISOString().slice(0, 10);
  const to = subtractDaysUtc(today, (input.page - 1) * LIST_DAYS_WINDOW_DAYS);
  const from = subtractDaysUtc(today, input.page * LIST_DAYS_WINDOW_DAYS - 1);
  const releasesRes = await fetchApprovedContent('release', from, to, 1, options);
  const hydrated = hydrateItems(releasesRes.items)
    .filter((r) => typeof r.date === 'string' && r.date <= today)
    .slice()
    .sort((a, b) => ((b.date ?? '') < (a.date ?? '') ? -1 : 1));
  const filtered = filterReleasesByQuery(hydrated, input.q);
  return {
    items: filtered,
    page: input.page,
    hasMore: releasesRes.hasMore,
  };
}

/**
 * Filter releases by a free-form query string. Empty / whitespace
 * queries pass everything through. Match is case-insensitive and
 * runs against the encoded-body title (or `headline` fallback) and
 * the decoded body copy.
 */
function filterReleasesByQuery(
  items: ReadonlyArray<ApprovedContentItem>,
  q: string | undefined,
): ReadonlyArray<ApprovedContentItem> {
  const trimmed = (q ?? '').trim().toLowerCase();
  if (trimmed.length === 0) return items;
  return items.filter((item) => {
    const title = ((item as { title?: string }).title ?? (item as { headline?: string }).headline ?? '').toLowerCase();
    const body = (item.body ?? '').toLowerCase();
    return title.includes(trimmed) || body.includes(trimmed);
  });
}

/**
 * Fetch a single approved content record by uuid via
 * `POST /GetApprovedContentByIdV1`. Used by the Game-page "Recent
 * coverage" panel (`content-ingestion-pipeline` Sub-R5.2 / R20.6):
 * the panel resolves the game row's `articles_mentioned` StringSet
 * (uuids) into hydrated News-record envelopes by fanning out one
 * `GetApprovedContentByIdV1` call per uuid.
 *
 * The live api-layer Handler (`services/src/api-layer/handlers/get-
 * content-by-id-handler.ts`) walks Facts → News → Releases GSIs by
 * uuid and returns the first hit whose `metadata.status` is in the
 * consumer-visible set (Property 6). A record not present in any
 * of the three tables surfaces as `undefined` — the caller filters
 * those out silently (a game row may legally reference a news uuid
 * that was rejected or has since been deleted; the "Recent
 * coverage" panel just shows the surviving subset).
 *
 * Errors other than 404 propagate so the caller can surface an
 * `<ApiUnavailableBanner>` per R8.12. The SWR cache reuses the
 * per-uuid response across game-page navigations.
 *
 * @param id The approved-content uuid to fetch.
 * @param options Base URL + API key sourced from `/config.json`.
 * @returns The hydrated content item, or `undefined` on 404.
 */
export async function getApprovedContentById(
  id: string,
  options: ContentApiClientOptions,
): Promise<ApprovedContentItem | undefined> {
  const response = await fetch(`${options.baseUrl}/GetApprovedContentByIdV1`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(options.apiKey ? { 'x-api-key': options.apiKey } : {}),
    },
    body: JSON.stringify({ id }),
  });
  if (response.status === 404) return undefined;
  if (!response.ok) throw new Error(`GetApprovedContentByIdV1 ${response.status}`);
  const body = (await response.json()) as { readonly item: ApprovedContentItem };
  return body.item;
}
